import { useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

import { DrawerItem } from 'helpers/drawer-config';

import NavBarItem from './NavBarItem';
import SearchBar from './SearchBar';
import MobileMenu from './MobileMenu';

type TopNavBarProps = {
  menuConfig: DrawerItem[];
  moreMenuConfig: DrawerItem[];
};

/**
 * TopNavBar Component
 *
 * Barra de navegación superior horizontal que reemplaza el drawer lateral.
 * Muestra items de menú principales con dropdowns y búsqueda jerárquica.
 *
 * @component
 * @param {TopNavBarProps} props - Las propiedades del componente
 * @returns {JSX.Element} El componente de barra de navegación superior
 */
function TopNavBar({
  menuConfig,
  moreMenuConfig
}: TopNavBarProps): JSX.Element {
  const { t } = useTranslation('global');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Barra de navegación principal - Desktop (inline) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          marginLeft: '-105px'
        }}
      >
        {/* Items de menú */}
        {menuConfig.map((item) => (
          <NavBarItem item={item} key={item.key} />
        ))}

        {/* Item "Más" */}
        <NavBarItem
          item={{
            key: 'topNav.more',
            title: t('topNav.more') || 'Más',
            icon: null,
            submenu: moreMenuConfig,
            permissions: []
          }}
        />

        {/* Búsqueda - integrada en el flujo */}
        <SearchBar allMenuConfig={[...menuConfig, ...moreMenuConfig]} />
      </Box>

      {/* Menú móvil */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        menuConfig={menuConfig}
        moreMenuConfig={moreMenuConfig}
        onClose={handleMobileMenuClose}
        onToggle={handleMobileMenuToggle}
      />
    </>
  );
}

export default TopNavBar;
